/**
 * Último toque de marketing antes de una compra: de qué campaña o buscador llegó
 * el comprador y en qué página aterrizó. Es lo que permite separar, compra a
 * compra, Google Ads (`gclid`, `utm_medium=cpc`) de Google orgánico
 * (`referrer_host` de Google sin `gclid`).
 *
 * Las claves van en snake_case a propósito: son el vocabulario estándar de las
 * UTM y la MISMA forma recorre los tres saltos —cuerpo del checkout, metadata
 * del proveedor de pago y propiedades de `purchase_completed`— sin traducirse.
 * Así PostHog las reconoce con los nombres que ya usa para sus eventos web.
 */
export interface MarketingAttribution {
  utm_source: string | null
  utm_medium: string | null
  utm_campaign: string | null
  /** Keyword configurada que activó el anuncio (`utm_term`, no la consulta literal del usuario). */
  utm_term: string | null
  gclid: string | null
  /** Camino (sin query ni hash) de la página de aterrizaje. */
  landing_path: string | null
  /** Host del referrer externo, sin camino: el camino de un buscador lleva la consulta. */
  referrer_host: string | null
}

export const EMPTY_MARKETING_ATTRIBUTION: MarketingAttribution = {
  utm_source: null,
  utm_medium: null,
  utm_campaign: null,
  utm_term: null,
  gclid: null,
  landing_path: null,
  referrer_host: null,
}

type AttributionKey = keyof MarketingAttribution

const UTM_MAX_LENGTH = 100

/**
 * Tope y forma por clave. Los topes caben de sobra en los 500 caracteres por
 * valor de la metadata de Stripe; un valor fuera de forma sale `null`, no
 * recortado: un dato a medias atribuiría a una campaña que no existe.
 */
const RULES: Record<AttributionKey, { maxLength: number; pattern?: RegExp; lowercase?: true }> = {
  utm_source: { maxLength: UTM_MAX_LENGTH },
  utm_medium: { maxLength: UTM_MAX_LENGTH },
  utm_campaign: { maxLength: UTM_MAX_LENGTH },
  utm_term: { maxLength: UTM_MAX_LENGTH },
  gclid: { maxLength: 200 },
  landing_path: { maxLength: 200, pattern: /^\/(?!\/)[^?#]*$/ },
  referrer_host: { maxLength: 100, pattern: /^[a-z0-9.-]+$/, lowercase: true },
}

const CONTROL_CHARACTERS = /[\u0000-\u001f\u007f]/

type Rule = (typeof RULES)[AttributionKey]

function fitsRule(value: string, { maxLength, pattern }: Rule): boolean {
  if (!value || value.length > maxLength || CONTROL_CHARACTERS.test(value)) return false
  return !pattern || pattern.test(value)
}

function normalizedValue(key: AttributionKey, raw: unknown): string | null {
  if (typeof raw !== 'string') return null
  const rule = RULES[key]
  const value = rule.lowercase ? raw.trim().toLowerCase() : raw.trim()
  return fitsRule(value, rule) ? value : null
}

/**
 * Lee la atribución de cualquier registro (el cuerpo de la petición o la
 * metadata que devuelve el webhook). Nunca lanza: la atribución es analítica y
 * jamás debe tumbar un checkout ni un cobro. Devuelve siempre las seis claves:
 * el literal lo comprueba el compilador, así que una clave nueva en la interfaz
 * no compila hasta que se lee aquí.
 */
export function parseMarketingAttribution(raw: unknown): MarketingAttribution {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return EMPTY_MARKETING_ATTRIBUTION
  const record = raw as Record<string, unknown>
  return {
    utm_source: normalizedValue('utm_source', record.utm_source),
    utm_medium: normalizedValue('utm_medium', record.utm_medium),
    utm_campaign: normalizedValue('utm_campaign', record.utm_campaign),
    utm_term: normalizedValue('utm_term', record.utm_term),
    gclid: normalizedValue('gclid', record.gclid),
    landing_path: normalizedValue('landing_path', record.landing_path),
    referrer_host: normalizedValue('referrer_host', record.referrer_host),
  }
}

/**
 * Las claves con valor, para la metadata del proveedor: Stripe no admite `null`
 * en metadata y una clave vacía sólo gasta cupo (50 claves por objeto).
 */
export function marketingAttributionMetadata(attribution: MarketingAttribution): Partial<Record<AttributionKey, string>> {
  const metadata: Partial<Record<AttributionKey, string>> = {}
  // `Object.keys` de un `Record<AttributionKey, …>` sólo puede dar esas claves.
  for (const key of Object.keys(RULES) as AttributionKey[]) {
    const value = attribution[key]
    if (value !== null) metadata[key] = value
  }
  return metadata
}
